import type { ReactNode } from "react";
import { Avatar } from "shared/components/Avatar";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  RefreshIcon,
  Cancel01Icon,
  Call02Icon,
  BubbleChatIcon,
  Chatting01Icon,
  Delete01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  AlertCircleIcon,
  CheckmarkCircle02Icon,
  Flag02Icon
} from "@hugeicons-pro/core-solid-rounded";
import { cx } from "shared/utils";
import { isMissedCall } from "shared/utils/missed-call";
import {
  PENDING_AUTO_RETRY_TITLE,
  pendingAutoRetryText
} from "shared/utils/sms-undeliverable";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent
} from "shared/components/Tooltip";
import { IconAI } from "shared/components/ai/IconAI";
import { NewTag } from "shared/app/SimilarUnit/NewTag";
import { MessageContent } from "./MessageContent";
import { MessageTranslateButton } from "./MessageTranslateButton";
import { channelConfig } from "./constants";
import { useInboxEmbed } from "../context/useInboxEmbed";
import { useCallFollowUpLabel } from "../context/followUpCallContext";
import { useMessageTranslation } from "../hooks/useMessageTranslation";
import { shouldCollapseEmailBody } from "../utils/emailCollapse";
import {
  type Attachment,
  type NoteDetails,
  type ReplyTarget,
  type VoiceDetails
} from "../types";
import type { MessageChannel, MessageStatus, SmsKeywordType } from "../types";

interface MessageHeaderProps {
  channel: MessageChannel;
  sentBy: string;
  timestamp: string;
  noteType?: string;
  isInbound?: boolean;
  /** Voice-only (CRM-1235): the incoming call was never picked up. */
  isMissedCall?: boolean;
  autoSent?: boolean;
  /** Part 2 similar unit notification: AI footer plus its tags. */
  similarUnitNotification?: boolean;
  escalationLabel?: string;
  translateControl?: ReactNode;
}

const SimilarUnitNotificationTags = () => (
  <>
    <span className="inline-flex h-[18px] items-center rounded-[5px] bg-primary-20 px-1.5 text-[11px] font-bold text-primary-600">
      ↻ Similar unit notification
    </span>
    <NewTag />
  </>
);

const EscalationTag = ({ label }: { label: string }) => (
  <>
    <div className="size-1 rounded-full bg-content-100" />
    <span className="flex items-center gap-1 text-danger-500">
      <HugeiconsIcon icon={Flag02Icon} size={14} className="text-danger-500" />
      {label}
    </span>
  </>
);

const TRANSLATABLE_CHANNELS: MessageChannel[] = ["sms", "chat", "email"];

const translateSitsInBubble = ({
  channel,
  isFromContact,
  receivedViaSms,
  emailCollapsible
}: {
  channel: MessageChannel;
  isFromContact: boolean;
  receivedViaSms?: boolean;
  emailCollapsible: boolean;
}): boolean => {
  if (channel === "email") return emailCollapsible;
  return channel === "chat" && isFromContact && Boolean(receivedViaSms);
};

const useMessageCardTranslation = ({
  channel,
  body,
  htmlBody,
  subject,
  isFromContact,
  receivedViaSms,
  isCompact
}: {
  channel: MessageChannel;
  body: string;
  htmlBody?: string;
  subject?: string;
  isFromContact: boolean;
  receivedViaSms?: boolean;
  isCompact: boolean;
}) => {
  const translation = useMessageTranslation({
    text: body,
    subject: channel === "email" ? subject : undefined,
    direction: isFromContact ? "inbound" : "outbound",
    enabled: TRANSLATABLE_CHANNELS.includes(channel) && Boolean(body.trim())
  });
  const translateInBubble = translateSitsInBubble({
    channel,
    isFromContact,
    receivedViaSms,
    emailCollapsible:
      isCompact && shouldCollapseEmailBody(body, htmlBody, isCompact)
  });
  const headerTranslateControl =
    translateInBubble || !translation.targetLanguage ? undefined : (
      <MessageTranslateButton translation={translation} />
    );
  return { translation, translateInBubble, headerTranslateControl };
};

const HeaderTranslateControl = ({
  control,
  dotClassName
}: {
  control?: ReactNode;
  dotClassName: string;
}) => (
  <Show if={Boolean(control)}>
    <div className={cx("size-1 rounded-full", dotClassName)} />
    {control}
  </Show>
);

// The pop-up drops the channel label and reads "From <name>" / "By <name>"
// next to the channel icon (2152:* wave brought the icon back). On the
// floating card an inbound message moves its timestamp under the bubble —
// the narrow meta row has no room for it — so it is skipped here.
const CompactMessageHeader = ({
  channel,
  sentBy,
  timestamp,
  isInbound,
  layout,
  escalationLabel,
  translateControl
}: Omit<MessageHeaderProps, "noteType"> & {
  layout?: "floating" | "drawer";
}) => {
  const compactIcon =
    channel === "chat"
      ? Chatting01Icon
      : (channelConfig[channel]?.icon ?? BubbleChatIcon);
  const timeBelowBubble = layout === "floating" && isInbound;
  return (
    <div className="flex items-center gap-2 text-xs font-medium text-content-300 opacity-80">
      <HugeiconsIcon
        icon={compactIcon}
        size={20}
        className="text-content-300"
      />
      <span>{isInbound ? `From ${sentBy}` : `By ${sentBy}`}</span>
      {timeBelowBubble ? null : (
        <>
          <div className="size-1 rounded-full bg-content-100" />
          <span className="text-content-200">{timestamp}</span>
        </>
      )}
      <HeaderTranslateControl
        control={translateControl}
        dotClassName="bg-content-100"
      />
      {escalationLabel ? <EscalationTag label={escalationLabel} /> : null}
    </div>
  );
};

// Inbound AI-agent calls surface as a note; the softphone's calls use the
// voice channel in both directions (CRM-811 outbound, CRM-905 inbound). All
// render with the call icon but distinct labels, and a missed inbound call
// turns the whole row danger red (CRM-1235).
const CallMessageHeader = ({
  isOutboundCall,
  isMissed,
  sentBy,
  timestamp,
  followUpLabel
}: {
  isOutboundCall: boolean;
  isMissed?: boolean;
  sentBy: string;
  timestamp: string;
  followUpLabel?: string;
}) => {
  const directionLabel = isOutboundCall ? "Outbound call" : "Incoming call";
  const toneClass = isMissed ? "text-danger-500" : "text-content-200";
  return (
    <div
      className={cx(
        "flex items-center gap-2 px-1 text-sm font-medium",
        toneClass
      )}
    >
      <HugeiconsIcon icon={Call02Icon} size={20} className={toneClass} />
      <span>{isMissed ? "Missed call" : directionLabel}</span>
      <div className="size-1 rounded-full bg-content-100" />
      <span>{isOutboundCall ? `Placed by ${sentBy}` : sentBy}</span>
      <div className="size-1 rounded-full bg-content-100" />
      <span>{timestamp}</span>
      {followUpLabel ? <EscalationTag label={followUpLabel} /> : null}
    </div>
  );
};

const MessageHeader = ({
  channel,
  sentBy,
  timestamp,
  noteType,
  isInbound,
  isMissedCall: isMissed,
  autoSent,
  similarUnitNotification,
  escalationLabel,
  translateControl
}: MessageHeaderProps) => {
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const isCallTranscript = channel === "note" && noteType === "call_transcript";

  if (isCompact && !isCallTranscript && channel !== "voice")
    return (
      <CompactMessageHeader
        channel={channel}
        sentBy={sentBy}
        timestamp={timestamp}
        isInbound={isInbound}
        layout={embed?.layout}
        escalationLabel={escalationLabel}
        translateControl={translateControl}
      />
    );

  if (isCallTranscript)
    return (
      <div className="flex items-center gap-2 px-1 text-sm font-medium text-content-200">
        <HugeiconsIcon icon={Call02Icon} size={16} />
        <span>Call</span>
        <div className="size-1 rounded-full bg-content-100" />
        <span>{timestamp}</span>
      </div>
    );

  if (channel === "voice")
    return (
      <CallMessageHeader
        isOutboundCall={channel === "voice" && !isInbound}
        isMissed={isMissed}
        sentBy={sentBy}
        timestamp={timestamp}
        followUpLabel={escalationLabel}
      />
    );

  // Fall back to a safe default so an unmapped channel (e.g. a newly added
  // backend channel the client doesn't know yet) never crashes the whole inbox
  // tree — that previously unmounted SoftphoneProvider and dropped live calls.
  const { icon: channelIcon, label: channelLabel } = channelConfig[channel] ?? {
    icon: BubbleChatIcon,
    label: channel
  };
  const isManual = channel === "note";
  const actionLabel = isManual ? "Recorded by" : "Sent by";

  return (
    <div className="flex items-center gap-2 px-1 text-xs font-medium text-content-300 opacity-80">
      <HugeiconsIcon
        icon={channelIcon}
        size={16}
        className="text-content-300"
      />
      <span>{channelLabel}</span>
      <div className="size-1 rounded-full bg-content-200" />
      <span>
        {autoSent || similarUnitNotification
          ? "Auto-sent by the AI leasing agent"
          : `${actionLabel} ${sentBy}`}
      </span>
      <div className="size-1 rounded-full bg-content-200" />
      <span>{timestamp}</span>
      <Show if={Boolean(similarUnitNotification)}>
        <SimilarUnitNotificationTags />
      </Show>
      <HeaderTranslateControl
        control={translateControl}
        dotClassName="bg-content-200"
      />
      {escalationLabel ? <EscalationTag label={escalationLabel} /> : null}
    </div>
  );
};

// "Not delivered" + retry/delete. The pop-up (design 2152:20847) stacks a
// 16px danger label over 34px buttons with a trash icon; /inbox keeps its
// original wide row.
const PendingAutoRetryActions = ({
  compact,
  autoRetryAt,
  onDelete
}: {
  compact: boolean;
  autoRetryAt: string;
  onDelete?: () => void;
}) => {
  const label = (
    <div
      className={cx(
        "flex cursor-default gap-2",
        compact ? "items-start" : "items-center"
      )}
    >
      <HugeiconsIcon
        icon={AlertCircleIcon}
        size={compact ? 16 : 20}
        className={cx("shrink-0 text-warning-700", compact && "mt-0.5")}
      />
      <div
        className={cx(compact ? "space-y-0.5" : "flex items-baseline gap-2")}
      >
        <p
          className={cx(
            "text-warning-700",
            compact ? "text-sm font-bold" : "text-base font-semibold"
          )}
        >
          {PENDING_AUTO_RETRY_TITLE}
        </p>
        <p
          className={cx(
            "font-normal text-content-600",
            compact ? "text-xs" : "text-sm"
          )}
        >
          {pendingAutoRetryText(autoRetryAt)}
        </p>
      </div>
    </div>
  );

  if (compact)
    return (
      <div className="flex w-full flex-col items-start gap-2">
        {label}
        <Button
          HugeIcon={Delete01Icon}
          variant={ButtonVariant.BASE}
          size={ButtonSize.XS}
          onClick={onDelete}
        >
          Delete
        </Button>
      </div>
    );

  return (
    <div className="flex items-center justify-between gap-4">
      {label}
      <Button
        HugeIcon={Cancel01Icon}
        variant={ButtonVariant.BASE}
        size={ButtonSize.BASE}
        iconStrokeWidth={3}
        onClick={onDelete}
      >
        Delete
      </Button>
    </div>
  );
};

const FailedMessageActions = ({
  compact,
  failureReason,
  retryAllowed,
  retryDisabledReason,
  autoRetryAt,
  onRetry,
  onDelete,
  isRetrying
}: {
  compact: boolean;
  failureReason?: string | null;
  retryAllowed?: boolean;
  retryDisabledReason?: string | null;
  autoRetryAt?: string | null;
  onRetry?: () => void;
  onDelete?: () => void;
  isRetrying?: boolean;
}) => {
  if (autoRetryAt)
    return (
      <PendingAutoRetryActions
        compact={compact}
        autoRetryAt={autoRetryAt}
        onDelete={onDelete}
      />
    );

  const retryButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Disabled elements don't fire hover events — the div keeps the
            tooltip alive. */}
        <div>
          <Button
            HugeIcon={RefreshIcon}
            variant={ButtonVariant.BASE}
            size={compact ? ButtonSize.XS : ButtonSize.BASE}
            onClick={onRetry}
            disabled={isRetrying || retryAllowed === false}
          >
            {isRetrying ? "Sending..." : "Try again"}
          </Button>
        </div>
      </TooltipTrigger>
      {retryAllowed === false && retryDisabledReason && (
        <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-3 text-sm text-white z-50">
          {retryDisabledReason}
        </TooltipContent>
      )}
    </Tooltip>
  );

  const notDeliveredLabel = (
    <Tooltip>
      <TooltipTrigger>
        {compact ? (
          <div className="flex cursor-default items-center gap-2 text-sm font-bold text-danger-500">
            <HugeiconsIcon icon={AlertCircleIcon} size={16} />
            <span>Not delivered</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-red-500 cursor-default">
            <HugeiconsIcon icon={AlertCircleIcon} size={20} />
            <span className="text-base font-semibold">Not delivered</span>
          </div>
        )}
      </TooltipTrigger>
      {failureReason && (
        <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-3 text-sm text-white z-50">
          {failureReason}
        </TooltipContent>
      )}
    </Tooltip>
  );

  if (compact)
    return (
      <div className="flex w-full flex-col items-start gap-2">
        {notDeliveredLabel}
        <div className="flex items-center gap-2">
          {retryButton}
          <Button
            HugeIcon={Delete01Icon}
            variant={ButtonVariant.BASE}
            size={ButtonSize.XS}
            onClick={onDelete}
            disabled={isRetrying}
          >
            Delete
          </Button>
        </div>
      </div>
    );

  return (
    <div className="flex items-center justify-between gap-4">
      {notDeliveredLabel}
      <div className="flex items-center gap-3">
        {retryButton}
        <Button
          HugeIcon={Cancel01Icon}
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          iconStrokeWidth={3}
          onClick={onDelete}
          disabled={isRetrying}
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

interface MessageCardLayoutProps {
  avatar: ReactNode;
  header: ReactNode;
  isFromContact: boolean;
  children: ReactNode;
}

export const MessageCardLayout = ({
  avatar,
  header,
  isFromContact,
  children
}: MessageCardLayoutProps) => {
  const isCompact = Boolean(useInboxEmbed()?.compact);

  return (
    <div
      className={cx(
        "relative flex items-start",
        isCompact ? "gap-3" : "gap-4",
        isFromContact ? "flex-row" : "flex-row-reverse"
      )}
    >
      <div className="flex-shrink-0">{avatar}</div>

      <div
        className={cx(
          "flex w-full min-w-0 flex-col",
          isCompact ? "gap-2" : "gap-2.5",
          isFromContact ? "items-start" : "items-end"
        )}
      >
        {children}
        {header}
      </div>
    </div>
  );
};

interface MessageAddress {
  name: string;
  address: string;
}

const formatOpenedAt = (iso: string): string | null => {
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
};

// Read-receipt tag on an outbound email the recipient opened (CRM-1299). Self-
// gates (email channel, outbound, has an open) so the caller stays simple. The
// tooltip states the open-tracking limitation so a missing tag isn't read as
// proof the lead ignored the email.
const OpenedReceipt = ({
  channel,
  isFromContact,
  openedAt
}: {
  channel: MessageChannel;
  isFromContact: boolean;
  openedAt?: string | null;
}) => {
  if (channel !== "email" || isFromContact || !openedAt) return null;

  const formatted = formatOpenedAt(openedAt);
  return (
    <Tooltip>
      <TooltipTrigger>
        <div className="flex items-center gap-1 text-xs font-medium text-success-600 cursor-default">
          <HugeiconsIcon icon={CheckmarkCircle02Icon} size={14} />
          <span>{formatted ? `Opened · ${formatted}` : "Opened"}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent className="max-w-[300px] rounded-lg border bg-alpha-800 p-3 text-sm text-white z-50">
        Open tracking relies on the recipient loading images. Some email clients
        block this, so a missing “Opened” tag doesn’t mean the email wasn’t
        read.
      </TooltipContent>
    </Tooltip>
  );
};

interface MessageCardProps {
  channel: MessageChannel;
  from?: MessageAddress;
  to?: MessageAddress;
  toAddresses?: string[];
  ccAddresses?: string[];
  bccAddresses?: string[];
  subject?: string;
  isReply?: boolean;
  sentViaQuotaFallback?: boolean;
  body: string;
  htmlBody?: string;
  timestamp: string;
  sentBy: string;
  avatar: {
    initials: string;
    color: string;
  };
  /** Outbound message sent from an accepted AI suggestion — shows the AI icon instead of the sender avatar. */
  isAiGenerated?: boolean;
  /** Outbound message the AI leasing agent auto-sent — labels the header "Auto-sent by the AI leasing agent". */
  autoSent?: boolean;
  /** Part 2 similar unit notification (source label) — AI footer plus tags. */
  similarUnitNotification?: boolean;
  isFromContact: boolean;
  messageId?: string;
  onReply?: (target: ReplyTarget) => void;
  noteType?: string;
  noteDetails?: NoteDetails;
  voiceDetails?: VoiceDetails | null;
  attachments?: Attachment[];
  sentAsSms?: boolean;
  sentAsSmsTo?: string | null;
  sentAsSmsFrom?: string | null;
  smsFailedReason?: string | null;
  receivedViaSms?: boolean;
  status?: MessageStatus;
  // When the recipient first opened this email (Nylas open tracking, CRM-1299);
  // renders an "Opened" read-receipt tag on outbound emails.
  openedAt?: string | null;
  smsKeywordType?: SmsKeywordType | null;
  failureReason?: string | null;
  retryAllowed?: boolean;
  retryDisabledReason?: string | null;
  autoRetryAt?: string | null;
  onRetry?: () => void;
  onDelete?: () => void;
  isRetrying?: boolean;
  // Voice-only: reopen the make-a-call modal to redial a failed outbound call.
  onVoiceRetry?: () => void;
  // Voice-only: inbound call rendering (CRM-905).
  isInbound?: boolean;
  /** Set on the inbound message that escalated the conversation (CRM-1438). */
  escalationLabel?: string;
}

const cardStateClassFor = ({
  isCompact,
  isFailed,
  isEscalated
}: {
  isCompact: boolean;
  isFailed: boolean;
  isEscalated: boolean;
}) => {
  if (isFailed) return isCompact ? "!border-danger-500" : "ring-2 ring-red-400";
  if (isEscalated)
    return isCompact
      ? "!border-danger-200"
      : "ring-4 ring-danger-50 !border-danger-200";
  return undefined;
};

export const MessageCard = ({
  channel,
  from,
  to,
  toAddresses,
  ccAddresses,
  bccAddresses,
  subject,
  isReply,
  sentViaQuotaFallback,
  body,
  htmlBody,
  timestamp,
  sentBy,
  avatar,
  isAiGenerated,
  autoSent,
  similarUnitNotification,
  isFromContact,
  messageId,
  onReply,
  noteType,
  noteDetails,
  voiceDetails,
  attachments,
  sentAsSms,
  sentAsSmsTo,
  sentAsSmsFrom,
  smsFailedReason,
  receivedViaSms,
  status,
  openedAt,
  smsKeywordType,
  failureReason,
  retryAllowed,
  retryDisabledReason,
  autoRetryAt,
  onRetry,
  onDelete,
  isRetrying,
  onVoiceRetry,
  isInbound,
  escalationLabel
}: MessageCardProps) => {
  // communication-service maps a no-answer/busy/failed call to Message.status
  // "failed", so without the channel guard the generic delivery-error block
  // would stack on top of the call card's own outcome line.
  const isFailed =
    channel !== "voice" && (status === "failed" || status === "bounced");
  // 32px avatars in the pop-up (design) against /inbox's 40px.
  const embed = useInboxEmbed();
  const isCompact = Boolean(embed?.compact);
  const avatarSize = isCompact ? 32 : 40;
  const isCallTranscript = channel === "note" && noteType === "call_transcript";
  const callFollowUpLabel = useCallFollowUpLabel(
    isCallTranscript ? noteDetails?.call_transcript_id : null
  );
  const cardStateClass = cardStateClassFor({
    isCompact,
    isFailed: isFailed && !autoRetryAt,
    isEscalated: Boolean(escalationLabel)
  });
  // Floating-card inbound messages carry their time under the bubble
  // (design 2152:9827); the meta row shows only "[icon] From <name>".
  const showTimeBelowBubble =
    isCompact &&
    embed?.layout === "floating" &&
    Boolean(isInbound) &&
    channel !== "voice" &&
    !isCallTranscript;

  // `isInbound` is deliberately undefined off the voice channel (MessagesList),
  // so the channel guard keeps a note or email from ever asking (CRM-1235).
  const isMissed =
    channel === "voice" && isMissedCall(voiceDetails, !!isInbound);

  const { translation, translateInBubble, headerTranslateControl } =
    useMessageCardTranslation({
      channel,
      body,
      htmlBody,
      subject,
      isFromContact,
      receivedViaSms,
      isCompact
    });

  return (
    <MessageCardLayout
      isFromContact={isFromContact}
      avatar={
        isAiGenerated ? (
          <IconAI bgSize={avatarSize} />
        ) : (
          <Avatar
            name={sentBy}
            size={avatarSize}
            fallbackText={avatar.initials}
            fallbackBackgroundColor={avatar.color}
            className={
              isCompact ? "!text-sm !font-bold" : "!text-base !font-black"
            }
          />
        )
      }
      header={
        <MessageHeader
          channel={channel}
          sentBy={sentBy}
          timestamp={timestamp}
          noteType={noteType}
          isInbound={isInbound}
          isMissedCall={isMissed}
          autoSent={autoSent}
          similarUnitNotification={similarUnitNotification}
          escalationLabel={escalationLabel ?? callFollowUpLabel}
          translateControl={headerTranslateControl}
        />
      }
    >
      <div
        className={cx(
          "flex w-full min-w-0 flex-col",
          isCompact ? "gap-2" : "gap-4",
          isFromContact ? "items-start" : "items-end"
        )}
      >
        <MessageContent
          channel={channel}
          isFromContact={isFromContact}
          body={body}
          subject={subject}
          isReply={isReply}
          sentViaQuotaFallback={sentViaQuotaFallback}
          from={from}
          htmlBody={htmlBody}
          to={to}
          toAddresses={toAddresses}
          ccAddresses={ccAddresses}
          bccAddresses={bccAddresses}
          messageId={messageId}
          onReply={onReply}
          noteType={noteType}
          noteDetails={noteDetails}
          attachments={attachments}
          sentAsSms={sentAsSms}
          sentAsSmsTo={sentAsSmsTo}
          sentAsSmsFrom={sentAsSmsFrom}
          smsFailedReason={smsFailedReason}
          receivedViaSms={receivedViaSms}
          smsKeywordType={smsKeywordType}
          className={cardStateClass}
          voiceDetails={voiceDetails}
          onVoiceRetry={onVoiceRetry}
          onDelete={onDelete}
          isInbound={isInbound}
          translation={translation}
          translateInBubble={translateInBubble}
        />

        {showTimeBelowBubble ? (
          <span className="self-end text-xs font-medium text-content-200">
            {timestamp}
          </span>
        ) : null}

        {isFailed ? (
          <FailedMessageActions
            compact={isCompact}
            failureReason={failureReason}
            retryAllowed={retryAllowed}
            retryDisabledReason={retryDisabledReason}
            autoRetryAt={autoRetryAt}
            onRetry={onRetry}
            onDelete={onDelete}
            isRetrying={isRetrying}
          />
        ) : null}
      </div>

      {status === "sending" && (
        <div className="text-xs text-content-200">Sending...</div>
      )}

      <OpenedReceipt
        channel={channel}
        isFromContact={isFromContact}
        openedAt={openedAt}
      />
    </MessageCardLayout>
  );
};
